import React, { useEffect, useState, useRef } from "react";
import { useEditor, useValue } from "@tldraw/tldraw";
import { useSelf } from "@liveblocks/react/suspense";

const Measure = () => {
  const [altPressed, setAltPressed] = useState(false);
  const [selectedShape, setSelectedShape] = useState<any>(null);
  const [hoveredShape, setHoveredShape] = useState<any>(null);
  const cursorPositionRef = useRef<{ x: number; y: number } | null>(null);

  const editor = useEditor();
  const currentUserCursorPosition = useSelf((me) => {
    const cursor = me.presence.cursor as { x: number; y: number } | null;
    if (!cursor) {
      return { x: 0, y: 0 }; // Fallback to a default position
    }
    return cursor;
  });

  const selectedShapeIds = useValue("isSelected", () => editor.getSelectedShapeIds(), [editor]);

  // Synchronize selected shape on selection change
  useEffect(() => {
    if (selectedShapeIds.length === 1) {
      const shape = editor.getShape(selectedShapeIds[0]);
      setTimeout(() => setSelectedShape(shape || null), 0);
    } else if (selectedShapeIds.length > 1) {
      // Multiple shapes selected - create a virtual shape with combined bounds
      const combinedBounds = editor.getSelectionPageBounds();
      if (combinedBounds) {
        // Create a virtual shape object with the combined bounds
        const virtualShape = {
          id: "virtual-selection",
          type: "group",
          x: combinedBounds.minX,
          y: combinedBounds.minY,
          props: {
            w: combinedBounds.width,
            h: combinedBounds.height,
          },
          // Store the actual bounds for accurate measurement
          _isVirtualGroup: true,
          _bounds: combinedBounds,
        };
        setTimeout(() => setSelectedShape(virtualShape), 0);
      } else {
        setTimeout(() => setSelectedShape(null), 0);
      }
    } else {
      setTimeout(() => setSelectedShape(null), 0);
    }
  }, [selectedShapeIds, editor]);

  // Register sideEffects to track shape changes
  useEffect(() => {
    // Above this threshold we skip the virtual-group recalculation per shape
    // mutation. Dragging 2000 shapes fires this handler 2000 times per
    // pointer move; the old `.includes()` was O(N) and `setSelectedShape`
    // triggered a React render each call → seconds of lag. The Measure
    // overlay isn't useful for bulk selections anyway. Keep in sync with
    // BULK_SELECTION_RENDER_THRESHOLD in the sidebars.
    const MEASURE_MULTI_SYNC_THRESHOLD = 30;

    // O(1) membership checks for the common multi-select case.
    const selectedShapeIdSet = new Set(selectedShapeIds);

    const handleShapeChange = (changedShape: any) => {
      // For single selection - update if the selected shape changes
      if (selectedShapeIds.length === 1 && selectedShape && changedShape.id === selectedShape.id) {
        setSelectedShape(changedShape);
      }
      // For multiple selection - if any of the selected shapes changes, recalculate the virtual group.
      // Skipped for bulk selections (>30) to keep multi-shape drag interactive.
      else if (
        selectedShapeIds.length > 1 &&
        selectedShapeIds.length <= MEASURE_MULTI_SYNC_THRESHOLD &&
        selectedShapeIdSet.has(changedShape.id)
      ) {
        const combinedBounds = editor.getSelectionPageBounds();
        if (combinedBounds) {
          const virtualShape = {
            id: "virtual-selection",
            type: "group",
            x: combinedBounds.minX,
            y: combinedBounds.minY,
            props: {
              w: combinedBounds.width,
              h: combinedBounds.height,
            },
            _isVirtualGroup: true,
            _bounds: combinedBounds,
          };
          setSelectedShape(virtualShape);
        }
      }
    };

    const unregisterAfterChange = editor.sideEffects.registerAfterChangeHandler("shape", handleShapeChange);

    return () => {
      unregisterAfterChange(); // Clean up sideEffect on unmount
    };
  }, [selectedShape, selectedShapeIds, editor]);

  // Track cursor position globally - ALWAYS
  useEffect(() => {
    const trackCursor = (e: MouseEvent) => {
      cursorPositionRef.current = { x: e.clientX, y: e.clientY };
    };

    window.addEventListener("mousemove", trackCursor);
    return () => window.removeEventListener("mousemove", trackCursor);
  }, []);

  // Manage the alt key press
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Alt" || e.key === "Option") {
        setAltPressed(true);
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === "Alt" || e.key === "Option") {
        setAltPressed(false);
        setHoveredShape(null);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, []);

  // Track mouse movement and detect shape under cursor when Alt is pressed
  useEffect(() => {
    if (!altPressed) {
      setHoveredShape(null);
      return;
    }

    // Helper to detect shape at specific point
    const detectShapeAt = (screenPoint: { x: number; y: number }) => {
      const pagePoint = editor.screenToPage(screenPoint);

      // Canvas infrastructure shapes to exclude
      const canvasInfrastructureIds = new Set([
        "shape:front",
        "shape:back",
        "shape:frontLabel",
        "shape:backLabel",
        "shape:frontRedLine",
        "shape:backRedLine",
        "shape:frontFoldLine",
        "shape:backFoldLine",
        "shape:frontBlackLine",
        "shape:backBlackLine",
        "shape:frontBorderDashLine",
        "shape:backBorderDashLine",
      ]);

      // Use TLDraw's optimized getShapeAtPoint API
      const hitShape = editor.getShapeAtPoint(pagePoint, {
        hitInside: true,
        hitLabels: false,
        margin: 0,
        renderingOnly: false,
      });

      // Validate the hit shape
      if (hitShape) {
        // Exclude customMeasure shapes
        if (hitShape.type === "customMeasure") return null;

        // Exclude selected shapes
        if (selectedShapeIds.includes(hitShape.id)) return null;

        // Exclude canvas infrastructure
        if (canvasInfrastructureIds.has(hitShape.id)) return null;

        return hitShape;
      }

      return null;
    };

    // Immediately detect shape when Alt is pressed (for Hover → Option case)
    if (cursorPositionRef.current) {
      const validShape = detectShapeAt(cursorPositionRef.current);
      if (validShape) {
        setHoveredShape(validShape);
      }
    }

    // Handle pointer move - detects shape at cursor position
    const handlePointerMove = (e: PointerEvent) => {
      const pagePoint = editor.screenToPage({ x: e.clientX, y: e.clientY });

      // Update hover state
      setHoveredShape((prev: any) => {
        // Get ALL shapes on the current page
        const allShapes = editor.getCurrentPageShapes();

        // Filter out customMeasure, selected shapes, and canvas infrastructure shapes
        const validShapesToTest = allShapes.filter((shape) => {
          if (shape.type === "customMeasure") return false;
          if (selectedShapeIds.length === 1 && shape.id === selectedShape?.id) return false;
          if (selectedShapeIds.length > 1 && selectedShapeIds.includes(shape.id)) return false;

          // Exclude canvas infrastructure shapes (canvas, labels, lines)
          const canvasInfrastructureIds = [
            "shape:front",
            "shape:back",
            "shape:frontLabel",
            "shape:backLabel",
            "shape:frontRedLine",
            "shape:backRedLine",
            "shape:frontFoldLine",
            "shape:backFoldLine",
            "shape:frontBlackLine",
            "shape:backBlackLine",
            "shape:frontBorderDashLine", // safety lines - we'll check these separately
            "shape:backBorderDashLine",
          ];

          if (canvasInfrastructureIds.includes(shape.id)) return false;

          return true;
        });

        // Find the first shape that contains the point (Z-order, top to bottom)
        for (const shape of validShapesToTest) {
          const bounds = editor.getShapePageBounds(shape);
          if (
            bounds &&
            pagePoint.x >= bounds.minX &&
            pagePoint.x <= bounds.maxX &&
            pagePoint.y >= bounds.minY &&
            pagePoint.y <= bounds.maxY
          ) {
            // Found a valid shape
            if (shape.id === prev?.id) return prev;
            return shape;
          }
        }

        // If no regular shape found, check safety lines manually (locked shapes)
        const safetyLineIds = ["shape:frontBorderDashLine", "shape:backBorderDashLine"];
        for (const safetyLineId of safetyLineIds) {
          const safetyLine = editor.getShape(safetyLineId as any);
          if (safetyLine) {
            const bounds = editor.getShapePageBounds(safetyLine);
            if (
              bounds &&
              pagePoint.x >= bounds.minX &&
              pagePoint.x <= bounds.maxX &&
              pagePoint.y >= bounds.minY &&
              pagePoint.y <= bounds.maxY
            ) {
              if (safetyLine.id === prev?.id) return prev;
              return safetyLine;
            }
          }
        }

        // No shape found at all
        return null;
      });
    };

    // Listen to pointer events on the editor container
    const container = editor.getContainer();
    container.addEventListener("pointermove", handlePointerMove);

    return () => {
      container.removeEventListener("pointermove", handlePointerMove);
    };
  }, [altPressed, selectedShape, selectedShapeIds, editor]);

  // Stop measure when clicking while Option is pressed
  useEffect(() => {
    if (!altPressed) {
      return;
    }

    const handlePointerDown = () => {
      // Stop the measure when user clicks
      setAltPressed(false);
      setHoveredShape(null);
    };

    const container = editor.getContainer();
    container.addEventListener("pointerdown", handlePointerDown);

    return () => {
      container.removeEventListener("pointerdown", handlePointerDown);
    };
  }, [altPressed, editor]);

  // Manage the measurement display between selected shape and hovered shape
  useEffect(() => {
    const existingShape = editor.getShape("shape:customMeasure" as any);

    const shouldCreateOrUpdate =
      altPressed &&
      selectedShape &&
      hoveredShape &&
      hoveredShape.id !== selectedShape.id && // Ensure hoveredShape is not the same as selectedShape
      currentUserCursorPosition &&
      !isCursorInsideSelectedShape(currentUserCursorPosition, selectedShape);

    if (shouldCreateOrUpdate) {
      const measurements = calculateMeasurements(selectedShape, hoveredShape);

      // Only create/update if there are valid measurements
      if (
        measurements &&
        (measurements.fromLeft || measurements.fromRight || measurements.fromTop || measurements.fromBottom)
      ) {
        if (!existingShape) {
          createCustomMeasureUtil(selectedShape, hoveredShape, measurements);
          // Use requestAnimationFrame to ensure it runs after TLDraw's render cycle
          requestAnimationFrame(() => {
            editor.bringToFront(["shape:customMeasure" as any]);
          });
        } else {
          updateCustomMeasureUtil(existingShape, selectedShape, hoveredShape, measurements);
          // Use requestAnimationFrame to ensure it runs after TLDraw's render cycle
          requestAnimationFrame(() => {
            editor.bringToFront(["shape:customMeasure" as any]);
          });
        }
      } else if (existingShape) {
        // Delete if no valid measurements
        editor.deleteShape("shape:customMeasure" as any);
      }
    } else if (existingShape && !shouldCreateOrUpdate) {
      editor.deleteShape("shape:customMeasure" as any);
    }
  }, [altPressed, selectedShape, hoveredShape, currentUserCursorPosition]);

  const isCursorInsideSelectedShape = (cursor: { x: number; y: number }, shape: any) => {
    if (!shape) return false;

    // Handle virtual group shapes
    const pageBounds = shape._isVirtualGroup ? shape._bounds : editor.getShapePageBounds(shape);
    if (!pageBounds) return false;

    return (
      cursor.x >= pageBounds.minX &&
      cursor.x <= pageBounds.maxX &&
      cursor.y >= pageBounds.minY &&
      cursor.y <= pageBounds.maxY
    );
  };

  /**
   * Calculate measurements between two shapes using their bounding boxes (Figma-style)
   *
   * Figma measures from the CENTER of each edge of the selected shape
   * to the corresponding edge of the hovered shape.
   *
   * Key logic:
   * - If an edge of A is within the range of B, measure to the SAME edge of B
   * - If A is completely separated from B, measure to the NEAREST edge
   * - Supports multiple selection (uses combined bounding box)
   */
  const calculateMeasurements = (shapeA: any, shapeB: any) => {
    // Get REAL bounding boxes in page coordinates (post-transformation)
    // For virtual groups (multiple selection), use the pre-calculated bounds
    const boundsA = shapeA._isVirtualGroup ? shapeA._bounds : editor.getShapePageBounds(shapeA);
    const boundsB = editor.getShapePageBounds(shapeB);

    if (!boundsA || !boundsB) return null;

    const DISTANCE_THRESHOLD = 1; // Minimum distance to show measurement (avoid noise)

    const measurements: {
      fromLeft: { distance: number; fromX: number; toX: number; y: number } | null;
      fromRight: { distance: number; fromX: number; toX: number; y: number } | null;
      fromTop: { distance: number; fromY: number; toY: number; x: number } | null;
      fromBottom: { distance: number; fromY: number; toY: number; x: number } | null;
      boundsA: typeof boundsA;
      boundsB: typeof boundsB;
    } = {
      fromLeft: null,
      fromRight: null,
      fromTop: null,
      fromBottom: null,
      boundsA,
      boundsB,
    };

    // Determine positional relationships
    const isLeftOfB = boundsA.maxX <= boundsB.minX; // A is completely to the left of B
    const isRightOfB = boundsA.minX >= boundsB.maxX; // A is completely to the right of B
    const isAboveB = boundsA.maxY <= boundsB.minY; // A is completely above B
    const isBelowB = boundsA.minY >= boundsB.maxY; // A is completely below B

    // Center points of each edge of shape A (selected shape)
    const leftEdgeCenter = { x: boundsA.minX, y: boundsA.center.y };
    const rightEdgeCenter = { x: boundsA.maxX, y: boundsA.center.y };
    const topEdgeCenter = { x: boundsA.center.x, y: boundsA.minY };
    const bottomEdgeCenter = { x: boundsA.center.x, y: boundsA.maxY };

    // ========== HORIZONTAL MEASUREMENTS ==========

    // Left edge of A → Left edge of B
    // Measure if:
    // - left edge of A is within horizontal range of B, OR
    // - B is completely within horizontal range of A (A contains B)
    const shouldMeasureLeft =
      (boundsA.minX >= boundsB.minX && boundsA.minX <= boundsB.maxX) ||
      (boundsB.minX >= boundsA.minX && boundsB.maxX <= boundsA.maxX);

    if (shouldMeasureLeft && !isLeftOfB && !isRightOfB) {
      const distanceLeftToLeft = Math.abs(leftEdgeCenter.x - boundsB.minX);
      if (distanceLeftToLeft > DISTANCE_THRESHOLD) {
        measurements.fromLeft = {
          distance: distanceLeftToLeft,
          fromX: leftEdgeCenter.x,
          toX: boundsB.minX,
          y: leftEdgeCenter.y,
        };
      }
    }

    // Right edge of A → Right edge of B
    // Measure if:
    // - right edge of A is within horizontal range of B, OR
    // - B is completely within horizontal range of A (A contains B)
    const shouldMeasureRight =
      (boundsA.maxX >= boundsB.minX && boundsA.maxX <= boundsB.maxX) ||
      (boundsB.minX >= boundsA.minX && boundsB.maxX <= boundsA.maxX);

    if (shouldMeasureRight && !isLeftOfB && !isRightOfB) {
      const distanceRightToRight = Math.abs(rightEdgeCenter.x - boundsB.maxX);
      if (distanceRightToRight > DISTANCE_THRESHOLD) {
        measurements.fromRight = {
          distance: distanceRightToRight,
          fromX: rightEdgeCenter.x,
          toX: boundsB.maxX,
          y: rightEdgeCenter.y,
        };
      }
    }

    // If A is completely to the left of B (no overlap)
    // Measure from right edge of A to left edge of B
    if (isLeftOfB) {
      const distance = boundsB.minX - rightEdgeCenter.x;
      if (distance > DISTANCE_THRESHOLD) {
        measurements.fromRight = {
          distance: distance,
          fromX: rightEdgeCenter.x,
          toX: boundsB.minX,
          y: rightEdgeCenter.y,
        };
      }
    }

    // If A is completely to the right of B (no overlap)
    // Measure from left edge of A to right edge of B
    if (isRightOfB) {
      const distance = leftEdgeCenter.x - boundsB.maxX;
      if (distance > DISTANCE_THRESHOLD) {
        measurements.fromLeft = {
          distance: distance,
          fromX: leftEdgeCenter.x,
          toX: boundsB.maxX,
          y: leftEdgeCenter.y,
        };
      }
    }

    // ========== VERTICAL MEASUREMENTS ==========

    // Top edge of A → Top edge of B
    // Measure if:
    // - top edge of A is within vertical range of B, OR
    // - B is completely within vertical range of A (A contains B)
    const shouldMeasureTop =
      (boundsA.minY >= boundsB.minY && boundsA.minY <= boundsB.maxY) ||
      (boundsB.minY >= boundsA.minY && boundsB.maxY <= boundsA.maxY);

    if (shouldMeasureTop && !isAboveB && !isBelowB) {
      const distanceTopToTop = Math.abs(topEdgeCenter.y - boundsB.minY);
      if (distanceTopToTop > DISTANCE_THRESHOLD) {
        measurements.fromTop = {
          distance: distanceTopToTop,
          fromY: topEdgeCenter.y,
          toY: boundsB.minY,
          x: topEdgeCenter.x,
        };
      }
    }

    // Bottom edge of A → Bottom edge of B
    // Measure if:
    // - bottom edge of A is within vertical range of B, OR
    // - B is completely within vertical range of A (A contains B)
    const shouldMeasureBottom =
      (boundsA.maxY >= boundsB.minY && boundsA.maxY <= boundsB.maxY) ||
      (boundsB.minY >= boundsA.minY && boundsB.maxY <= boundsA.maxY);

    if (shouldMeasureBottom && !isAboveB && !isBelowB) {
      const distanceBottomToBottom = Math.abs(bottomEdgeCenter.y - boundsB.maxY);
      if (distanceBottomToBottom > DISTANCE_THRESHOLD) {
        measurements.fromBottom = {
          distance: distanceBottomToBottom,
          fromY: bottomEdgeCenter.y,
          toY: boundsB.maxY,
          x: bottomEdgeCenter.x,
        };
      }
    }

    // If A is completely above B (no overlap)
    // Measure from bottom edge of A to top edge of B
    if (isAboveB) {
      const distance = boundsB.minY - bottomEdgeCenter.y;
      if (distance > DISTANCE_THRESHOLD) {
        measurements.fromBottom = {
          distance: distance,
          fromY: bottomEdgeCenter.y,
          toY: boundsB.minY,
          x: bottomEdgeCenter.x,
        };
      }
    }

    // If A is completely below B (no overlap)
    // Measure from top edge of A to bottom edge of B
    if (isBelowB) {
      const distance = topEdgeCenter.y - boundsB.maxY;
      if (distance > DISTANCE_THRESHOLD) {
        measurements.fromTop = {
          distance: distance,
          fromY: topEdgeCenter.y,
          toY: boundsB.maxY,
          x: topEdgeCenter.x,
        };
      }
    }

    return measurements;
  };

  const createCustomMeasureUtil = (startShape: any, endShape: any, measurements: any) => {
    const { fromLeft, fromRight, fromTop, fromBottom, boundsA, boundsB } = measurements;

    // Calculate the overall bounding box for the measure shape
    const allPoints = [
      boundsA.minX,
      boundsA.maxX,
      boundsB.minX,
      boundsB.maxX,
      fromLeft?.fromX,
      fromLeft?.toX,
      fromRight?.fromX,
      fromRight?.toX,
    ].filter((x) => x !== undefined && x !== null);

    const allPointsY = [
      boundsA.minY,
      boundsA.maxY,
      boundsB.minY,
      boundsB.maxY,
      fromTop?.fromY,
      fromTop?.toY,
      fromBottom?.fromY,
      fromBottom?.toY,
    ].filter((y) => y !== undefined && y !== null);

    const minX = Math.min(...allPoints);
    const maxX = Math.max(...allPoints);
    const minY = Math.min(...allPointsY);
    const maxY = Math.max(...allPointsY);

    const width = maxX - minX;
    const height = maxY - minY;

    editor.createShape({
      type: "customMeasure",
      id: "shape:customMeasure" as any,
      x: minX,
      y: minY,
      isLocked: false,
      props: {
        w: width,
        h: height,
        // Store relative coordinates for rendering
        fromLeft: fromLeft
          ? {
              distance: Math.round(fromLeft.distance),
              fromX: fromLeft.fromX - minX,
              toX: fromLeft.toX - minX,
              y: fromLeft.y - minY,
            }
          : null,
        fromRight: fromRight
          ? {
              distance: Math.round(fromRight.distance),
              fromX: fromRight.fromX - minX,
              toX: fromRight.toX - minX,
              y: fromRight.y - minY,
            }
          : null,
        fromTop: fromTop
          ? {
              distance: Math.round(fromTop.distance),
              fromY: fromTop.fromY - minY,
              toY: fromTop.toY - minY,
              x: fromTop.x - minX,
            }
          : null,
        fromBottom: fromBottom
          ? {
              distance: Math.round(fromBottom.distance),
              fromY: fromBottom.fromY - minY,
              toY: fromBottom.toY - minY,
              x: fromBottom.x - minX,
            }
          : null,
        boundsA: {
          x: boundsA.minX - minX,
          y: boundsA.minY - minY,
          w: boundsA.width,
          h: boundsA.height,
        },
        boundsB: {
          x: boundsB.minX - minX,
          y: boundsB.minY - minY,
          w: boundsB.width,
          h: boundsB.height,
        },
      },
    });
  };

  const updateCustomMeasureUtil = (existingShape: any, startShape: any, endShape: any, measurements: any) => {
    const { fromLeft, fromRight, fromTop, fromBottom, boundsA, boundsB } = measurements;

    // Calculate the overall bounding box for the measure shape
    const allPoints = [
      boundsA.minX,
      boundsA.maxX,
      boundsB.minX,
      boundsB.maxX,
      fromLeft?.fromX,
      fromLeft?.toX,
      fromRight?.fromX,
      fromRight?.toX,
    ].filter((x) => x !== undefined && x !== null);

    const allPointsY = [
      boundsA.minY,
      boundsA.maxY,
      boundsB.minY,
      boundsB.maxY,
      fromTop?.fromY,
      fromTop?.toY,
      fromBottom?.fromY,
      fromBottom?.toY,
    ].filter((y) => y !== undefined && y !== null);

    const minX = Math.min(...allPoints);
    const maxX = Math.max(...allPoints);
    const minY = Math.min(...allPointsY);
    const maxY = Math.max(...allPointsY);

    const width = maxX - minX;
    const height = maxY - minY;

    editor.updateShape({
      ...existingShape,
      x: minX,
      y: minY,
      props: {
        w: width,
        h: height,
        fromLeft: fromLeft
          ? {
              distance: Math.round(fromLeft.distance),
              fromX: fromLeft.fromX - minX,
              toX: fromLeft.toX - minX,
              y: fromLeft.y - minY,
            }
          : null,
        fromRight: fromRight
          ? {
              distance: Math.round(fromRight.distance),
              fromX: fromRight.fromX - minX,
              toX: fromRight.toX - minX,
              y: fromRight.y - minY,
            }
          : null,
        fromTop: fromTop
          ? {
              distance: Math.round(fromTop.distance),
              fromY: fromTop.fromY - minY,
              toY: fromTop.toY - minY,
              x: fromTop.x - minX,
            }
          : null,
        fromBottom: fromBottom
          ? {
              distance: Math.round(fromBottom.distance),
              fromY: fromBottom.fromY - minY,
              toY: fromBottom.toY - minY,
              x: fromBottom.x - minX,
            }
          : null,
        boundsA: {
          x: boundsA.minX - minX,
          y: boundsA.minY - minY,
          w: boundsA.width,
          h: boundsA.height,
        },
        boundsB: {
          x: boundsB.minX - minX,
          y: boundsB.minY - minY,
          w: boundsB.width,
          h: boundsB.height,
        },
      },
    });
  };

  return <div></div>;
};

export default Measure;
